export function PrivacyPage() {
  return (
    <article>
      <h1>Privacy</h1>

      <p>
        We are <strong>Webhooks Ltd</strong> — a UK private limited company, registered in England and Wales,
        company number <strong>16791194</strong>, registered office 8 Cedar Way, Tonyrefail, Porth, Wales CF39 8JN
        (see our <a href="/company">company page</a>). When you use Guess My Time we are the{' '}
        <strong>data controller</strong> for everything described below.
      </p>
      <p>
        Our ICO registration number is <strong>{/* TODO(mark): paste the ICO Z-number here */}[pending]</strong>.
        For data questions or to exercise any of the rights below, email{' '}
        <a href="mailto:hello@guessmytime.com">hello@guessmytime.com</a>. You also have the right to
        complain to the UK Information Commissioner's Office at{' '}
        <a href="https://ico.org.uk" target="_blank" rel="noopener">ico.org.uk</a>.
      </p>

      <h2>What we store, and why we're allowed to</h2>
      <p>UK GDPR requires us to tell you our lawful basis for each thing we process. Here it is, line by line.</p>
      <ul style={{ paddingLeft: 18 }}>
        <li>
          <strong>Your email, if you sign in as an organiser.</strong> Needed to identify your account and send
          you a sign-in code or winner notification. Lawful basis: <em>contract</em> (UK GDPR Art. 6(1)(b)).
        </li>
        <li>
          <strong>Your display name, if you set one.</strong> Shown on the public page of your sweepstakes as
          "Organised by …". Lawful basis: <em>contract</em>.
        </li>
        <li>
          <strong>Your guess</strong> — display name, the time you guessed, the timestamp, optionally your email
          (only used to tell you if you win). Needed to render the public leaderboard and decide the winner.
          Lawful basis: <em>legitimate interests</em> (Art. 6(1)(f)) — running the sweepstake the organiser created.
        </li>
        <li>
          <strong>A daily-rotated hash of your IP address.</strong> SHA-256 of your IP combined with a salt that
          rotates every day. We use it only to flag soft duplicates ("looks like the same person guessed twice").
          We never store the raw IP. Lawful basis: <em>legitimate interests</em> (abuse prevention).
        </li>
        <li>
          <strong>The sign-in and antiforgery cookies.</strong> Required for the site to function — exempt from
          consent under PECR (the UK ePrivacy rules).
        </li>
        <li>
          <strong>A cached snapshot of an organiser's fundraising page</strong> when they choose to wire one. We
          fetch publicly-available information from the fundraising platform's API (charity name, page title,
          amount raised in GBP, target, currency, and up to 10 most recent <em>donor first names + amounts</em>)
          and show it on the participant page. Lawful basis: <em>legitimate interests</em> (Art. 6(1)(f)) —
          showing live fundraising progress to participants the organiser invited. The data we cache is the same
          data the fundraising platform already shows publicly to anyone who visits the page directly.
        </li>
      </ul>

      <h2>What we don't do</h2>
      <ul style={{ paddingLeft: 18 }}>
        <li>
          Guess My Time <strong>does not process donations</strong>. When you tap "Donate" you're sent to the
          fundraiser's own donation page. We never see the money.
        </li>
        <li>We don't sell, share, or rent your data to anyone.</li>
        <li>We don't run advertising and don't build advertising profiles.</li>
        <li>
          We don't knowingly process data from children under 13. If you're an adult helping a child enter, do so
          in your own name.
        </li>
      </ul>

      <h2>Who we share it with</h2>
      <p>
        Some of the work above runs on third-party infrastructure. Each one is a processor acting on our instructions:
      </p>
      <ul style={{ paddingLeft: 18 }}>
        <li>
          <strong>Brevo</strong> (France) delivers our transactional emails — sign-in codes and winner
          notifications. Transfer covered by the UK's adequacy decision for the EU.
        </li>
        <li>
          <strong>Google</strong> (US) serves Google Tag Manager and Analytics 4, and only when you grant
          consent. Transfer covered by the UK extension to the EU–US Data Privacy Framework.
        </li>
        <li>
          <strong>Cloudflare</strong> (US/global) sits in front of our origin, terminating TLS and caching
          static assets at the edge. Transfer covered by the UK International Data Transfer Agreement.
        </li>
        <li>
          <strong>JustGiving</strong> (a Blackbaud product) is the fundraising platform we read from when an
          organiser wires a JustGiving page to their sweepstake. We only ever <em>read</em> public data; we
          don't send personal data to JustGiving. JustGiving operates its own UK GDPR posture as a separate
          controller for the data it holds on the page.
        </li>
        <li>
          Our origin runs on hardware we control, in the UK. No third party.
        </li>
      </ul>

      <h2>Cookies and tags</h2>
      <p>
        We use Google Tag Manager to load a small number of measurement tags. The choices you make in the
        cookie banner control what those tags can do. By default everything is denied. You can change your
        mind any time via "Manage cookies" in the footer.
      </p>
      <p>
        Under Google Consent Mode v2, each category is signalled separately. We've expanded each below — in
        plain English first, then the Consent Mode terminology in case you're cross-referencing.
      </p>
      <ul style={{ paddingLeft: 18 }}>
        <li>
          <strong>Strictly necessary</strong> (always on). The sign-in cookie and the antiforgery token. Without
          these the site can't keep you signed in or stop cross-site form forgery. Exempt from consent under PECR.
        </li>
        <li>
          <strong>Analytics.</strong> Page-view and event counts so we can see which screens people get stuck on.
          We don't store your IP for analytics — Google Analytics truncates it before logging — and we never see a
          name attached to a visit. When this category is denied, the same tags still load but run in cookieless
          mode so we keep aggregate hit counts without per-visitor tracking. <em>Consent Mode: allows analytics_storage.</em>
          When granted, GA4's Enhanced Measurement is enabled on our data stream, which auto-collects scroll depth,
          outbound link clicks, file downloads, site-search terms, video interactions, and form starts/submissions,
          all attached to a pseudonymous client identifier.
        </li>
        <li>
          <strong>Functionality.</strong> Small preferences remembered between visits, like dismissed banners.
          <em> Consent Mode: allows functionality_storage.</em>
        </li>
        <li>
          <strong>Personalisation.</strong> Tailoring the site to past activity. We don't currently use this slot;
          granting it has no effect today. We've left it in the banner so future use is opt-in by default.{' '}
          <em>Consent Mode: allows personalization_storage.</em>
        </li>
        <li>
          <strong>Advertising.</strong> Storage that supports ad tags. We don't run ads today, so granting it has
          no effect. We've left it in the banner so future use is opt-in by default.{' '}
          <em>Consent Mode: allows ad_storage.</em>
        </li>
        <li>
          <strong>Advertising, user data.</strong> Sharing data with Google for ad measurement and audiences. Off
          by default and no effect until we run ads. <em>Consent Mode: allows ad_user_data.</em>
        </li>
        <li>
          <strong>Advertising, personalisation.</strong> Tailoring ad content to past behaviour. Off by default
          and no effect until we run ads. <em>Consent Mode: allows ad_personalization.</em>
        </li>
      </ul>
      <p>
        Your choice is stored locally in your browser under the key <code>gmt:consent:v1</code>. Clearing site
        data resets the choice and the banner will re-appear on your next visit.
      </p>

      <h2>How long we keep things</h2>
      <ul style={{ paddingLeft: 18 }}>
        <li>
          <strong>Organiser accounts.</strong> Kept while active. If you haven't signed in for 24 months we'll
          email a warning and then delete the account, which cascade-deletes every sweepstake and guess you created.
          You can also delete your account yourself from your dashboard at any time.
        </li>
        <li>
          <strong>Participant emails.</strong> Automatically purged 30 days after the parent sweepstake is resolved.
        </li>
        <li>
          <strong>The rest of a guess</strong> — name, time, timestamp, IP hash. Kept while the sweepstake exists.
          Delete the sweepstake and the guesses go with it.
        </li>
        <li>
          <strong>Cached fundraising-page snapshots.</strong> Replaced (overwritten in place) on the next refresh —
          typically within 5 minutes. We show no figures from a snapshot older than 24 hours. Deleted when the
          organiser unwires the page, when the sweepstake is archived, or when the sweepstake is deleted.
        </li>
      </ul>

      <h2>Your rights</h2>
      <p>You have the right to:</p>
      <ul style={{ paddingLeft: 18 }}>
        <li>Ask us what we hold about you (access).</li>
        <li>Have anything inaccurate corrected (rectification).</li>
        <li>Have your data deleted (erasure / "right to be forgotten").</li>
        <li>Restrict or object to our processing.</li>
        <li>Receive your data in a portable format.</li>
        <li>Withdraw any consent you've given — without affecting things we already lawfully did with it.</li>
      </ul>
      <p>
        To exercise any of these, email <a href="mailto:hello@guessmytime.com">hello@guessmytime.com</a>.
        We'll respond within 30 days. Organisers can also delete their account themselves from the dashboard.
        If you're a participant and a sweepstake organiser hasn't responded to a request, email us directly —
        we'll handle it.
      </p>
      <p>
        If you're not happy with how we've handled your request, you can complain to the UK Information
        Commissioner's Office at <a href="https://ico.org.uk" target="_blank" rel="noopener">ico.org.uk</a>.
      </p>

      <h2>Security</h2>
      <p>
        We use HTTPS everywhere and passwordless sign-in, so there's no password to steal. If you think you've
        found a security issue, please email <a href="mailto:hello@guessmytime.com">hello@guessmytime.com</a> —
        we take responsible disclosure seriously.
      </p>

      <h2>Changes to this notice</h2>
      <p>
        If we make material changes we'll email signed-in organisers at least 14 days before the new version takes
        effect. The version date is at the bottom of this page.
      </p>

      <h2>Contact</h2>
      <p>
        <a href="mailto:hello@guessmytime.com">hello@guessmytime.com</a>.
      </p>

      <p className="muted" style={{ fontSize: 14, marginTop: 24 }}>
        Last updated: 2026-05-14.
      </p>
    </article>
  );
}
